import { useEffect, useMemo, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, Square, Clock, Search, X } from "lucide-react";
import { toast } from "sonner";

const ACTIVITIES = ["Rådgivning", "Bokføring", "MVA", "Årsregnskap"];

const two = (n) => String(n).padStart(2, "0");
const fmtHMS = (s) => `${two(Math.floor(s / 3600))}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}`;
const fmtHM = (hoursDecimal) => {
  const mins = Math.round((hoursDecimal || 0) * 60);
  return `${two(Math.floor(mins / 60))}:${two(mins % 60)}`;
};

export default function Timer() {
  const [customers, setCustomers] = useState([]);
  const [recentIds, setRecentIds] = useState([]);
  const [active, setActive] = useState(null);
  const [today, setToday] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const loadOverview = useCallback(() => {
    api.get("/timer/overview").then((r) => { setActive(r.data.active); setToday(r.data.today || {}); }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/customers")
      .then((r) => setCustomers([...r.data].sort((a, b) => (a.legal_name || "").localeCompare(b.legal_name || "", "nb"))))
      .catch(() => {});
    api.get("/time-entries/recent-customers", { params: { limit: 20 } })
      .then((r) => setRecentIds((r.data || []).map((x) => x.customer_id)))
      .catch(() => {});
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!active?.started_at) { setElapsed(0); return; }
    const startMs = new Date(active.started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  const start = async (c, activity) => {
    setBusy(true);
    try {
      const r = await api.post("/timer/start", { customer_id: c.id, activity });
      setActive(r.data.active); setToday(r.data.today || {});
      if (r.data.logged) toast.success(`Førte ${r.data.logged.hours} t på forrige kunde`);
      toast.success(`Timer startet — ${activity} på ${c.legal_name}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const r = await api.post("/timer/stop");
      setActive(null); setToday(r.data.today || {});
      if (r.data.logged) toast.success(`Førte ${r.data.logged.hours} t på ${r.data.logged.customer_name || "kunde"}`);
      else toast.message("Timer stoppet – under 1 min, ingen tid ført");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const term = q.trim().toLowerCase();

  const { recent, rest } = useMemo(() => {
    const byId = Object.fromEntries(customers.map((c) => [c.id, c]));
    const recentSet = new Set(recentIds);
    const recentList = recentIds.map((id) => byId[id]).filter(Boolean);
    const restList = customers.filter((c) => !recentSet.has(c.id));
    return { recent: recentList, rest: restList };
  }, [customers, recentIds]);

  const match = (c) => !term || c.legal_name.toLowerCase().includes(term) || (c.org_number || "").includes(term);
  const filteredRecent = recent.filter(match);
  const filteredRest = rest.filter(match);
  const nothing = filteredRecent.length === 0 && filteredRest.length === 0;

  const Row = ({ c }) => {
    const th = today[c.id] || 0;
    const activeHere = active?.customer_id === c.id;
    return (
      <div data-testid={`timer-row-${c.id}`}
        className={`rounded-xl border p-4 transition-colors ${activeHere ? "border-emerald-500/50 bg-emerald-500/[0.06]" : "border-border bg-card hover:border-white/20"}`}>
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="min-w-0">
            <p className="font-medium truncate">{c.legal_name}</p>
            <p className="text-xs text-muted-foreground" data-testid={`timer-today-${c.id}`}>
              {th > 0 ? `Totalt registrert i dag ${fmtHM(th)}` : "Ingen timer i dag"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {ACTIVITIES.map((act) => {
            const running = activeHere && (active?.activity || "") === act;
            return running ? (
              <Button key={act} size="sm" onClick={stop} disabled={busy}
                className="bg-destructive text-white hover:bg-destructive/90 font-mono tabular-nums"
                data-testid={`timer-stop-${c.id}-${act}`}>
                <Square className="w-4 h-4 mr-1.5" /> {act} · {fmtHMS(elapsed)}
              </Button>
            ) : (
              <Button key={act} size="sm" variant="secondary" onClick={() => start(c, act)} disabled={busy}
                data-testid={`timer-start-${c.id}-${act}`}>
                <Play className="w-4 h-4 mr-1.5" /> {act}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Page>
      <PageHeader title="Timer" subtitle="Velg kunde og aktivitet — start og stopp" testid="timer-header">
        {active && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 pl-4 pr-2 py-2" data-testid="active-timer-bar">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <Play className="w-4 h-4 text-emerald-300" strokeWidth={1.75} />
            <div className="text-sm leading-tight">
              <div className="font-medium">{active.customer_name}{active.activity ? ` · ${active.activity}` : ""}</div>
              <div className="font-mono tabular-nums text-emerald-300" data-testid="active-timer-elapsed">{fmtHMS(elapsed)}</div>
            </div>
            <Button size="sm" onClick={stop} disabled={busy} className="ml-2 bg-destructive text-white hover:bg-destructive/90" data-testid="active-timer-stop"><Square className="w-4 h-4 mr-1" /> Stopp</Button>
          </div>
        )}
      </PageHeader>

      <div className="relative w-full sm:w-80 mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk kunde…" className="pl-9 pr-9 bg-secondary border-border" data-testid="timer-search" />
        {q && (
          <button type="button" onClick={() => setQ("")} aria-label="Tøm søk"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
            data-testid="timer-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {nothing ? (
        <Empty icon={Clock} title="Ingen kunder" hint="Ingen kunder å føre timer på." />
      ) : (
        <div className="space-y-6" data-testid="timer-customer-list">
          {filteredRecent.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="timer-recent-heading">Siste kunder</p>
              {filteredRecent.map((c) => <Row key={c.id} c={c} />)}
            </div>
          )}
          {filteredRest.length > 0 && (
            <div className="space-y-2">
              {filteredRecent.length > 0 && (
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="timer-all-heading">Alle kunder</p>
              )}
              {filteredRest.map((c) => <Row key={c.id} c={c} />)}
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
